import { useLanguage } from "@/context/LanguageContext";
import SectionLabel from "@/components/SectionLabel";
import {
  ClipboardList,
  Search,
  FileText,
  BellRing,
  TrendingUp,
  Pin,
  Check,
  Grid3x3,
} from "lucide-react";

const KIND_ICONS = {
  prepare: ClipboardList,
  retrieve: Search,
  summarize: FileText,
  follow_up: BellRing,
  analyze: TrendingUp,
  track: Pin,
};

const BADGE_TONE = {
  prepare: { bg: "bg-emerald-500/12", text: "text-emerald-300", dot: "bg-emerald-400" },
  retrieve: { bg: "bg-sky-500/12", text: "text-sky-300", dot: "bg-sky-400" },
  summarize: { bg: "bg-violet-500/12", text: "text-violet-300", dot: "bg-violet-400" },
  follow_up: { bg: "bg-amber-500/12", text: "text-amber-300", dot: "bg-amber-400" },
  analyze: { bg: "bg-cyan-500/12", text: "text-cyan-300", dot: "bg-cyan-400" },
  track: { bg: "bg-rose-500/12", text: "text-rose-300", dot: "bg-rose-400" },
};

export default function UseCasesGrid() {
  const { t } = useLanguage();
  const items = t("usecases.items");

  return (
    <section
      id="cases-grid"
      data-testid="usecases-grid-section"
      className="relative overflow-hidden bg-[#1A1814] text-white border-t border-white/5"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div
          className="absolute top-[-15%] right-[-10%] w-[520px] h-[520px] opacity-22"
          style={{
            background:
              "radial-gradient(circle, rgba(67,125,234,0.55) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
        <div
          className="absolute bottom-[-10%] left-[-8%] w-[480px] h-[480px] opacity-18"
          style={{
            background:
              "radial-gradient(circle, rgba(244,199,161,0.4) 0%, transparent 70%)",
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="container-iko relative py-24 lg:py-32">
        <div className="max-w-2xl mb-14 lg:mb-20">
          <SectionLabel icon={Grid3x3} onDark>{t("usecases.eyebrow")}</SectionLabel>
          <h2 className="font-display font-semibold text-4xl lg:text-6xl leading-[1.02] tracking-[-0.034em] text-balance mb-5">
            {t("usecases.title")}
          </h2>
          <p className="text-base lg:text-lg text-white/65 leading-relaxed max-w-xl">
            {t("usecases.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {items.map((it, i) => {
            const Icon = KIND_ICONS[it.kind] || ClipboardList;
            const tone = BADGE_TONE[it.kind] || BADGE_TONE.prepare;
            return (
              <article
                key={it.kind}
                data-testid={`usecase-card-${i}`}
                className="group relative rounded-3xl bg-white/[0.04] border border-white/10 overflow-hidden hover:bg-white/[0.06] hover:border-white/15 hover:-translate-y-1 transition-all duration-500 backdrop-blur"
              >
                <div className="absolute -top-12 -right-10 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(67,125,234,0.35) 0%, transparent 70%)",
                    filter: "blur(20px)",
                  }}
                />

                <div className="relative p-6 lg:p-7 flex flex-col h-full min-h-[300px]">
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/15 flex items-center justify-center text-[#8F9FFE] shadow-[0_4px_14px_-4px_rgba(67,125,234,0.3)]">
                      <Icon size={19} strokeWidth={1.8} />
                    </div>
                  </div>

                  <h3 className="font-display font-semibold text-2xl lg:text-[26px] tracking-[-0.025em] mb-4 text-white leading-tight">
                    {it.name}
                  </h3>

                  {/* User bubble */}
                  <div className="mb-5">
                    <div className="inline-block max-w-[95%] px-3.5 py-2 bg-white/[0.08] border border-white/12 rounded-2xl rounded-bl-md text-[13.5px] leading-snug text-white/90">
                      {it.ask}
                    </div>
                  </div>

                  {/* Actions checklist */}
                  <div className="space-y-2 mb-6">
                    {it.actions.map((a, j) => (
                      <div
                        key={j}
                        className="flex items-center gap-2.5 text-[13px] text-white/75 transition-all"
                        style={{ transitionDelay: `${j * 60}ms` }}
                      >
                        <div className="shrink-0 w-4 h-4 rounded-full bg-white/[0.06] border border-white/15 group-hover:bg-[#437DEA] group-hover:border-[#437DEA] flex items-center justify-center transition-colors duration-500">
                          <Check size={9} strokeWidth={3} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <span>{a}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 border-t border-white/8">
                    <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full ${tone.bg} ${tone.text} text-[12.5px] font-semibold`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                      {it.badge}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
